import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StockTableLayout, Statistic } from '../../components/StockTable';
import { useTickerSnapshot } from '../../hooks/useTickerSnapshot';
import { syncApiService } from '../../services/syncApi';
import { TickerDisplayData } from '../../types/ticker';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import { searchApiService, MarketStatusResponse } from '../../services/searchApi';
import TabBar from '../../components/Common/TabBar';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import ShareListModal from '../../components/ShareListModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { useDailyTabStatistics, formatDailyStatisticValue, getDailyStatisticColor } from '../../hooks/useDailyTabStatistics';
import { getCachedLabel, getLatestLabel, getOpenLabel } from '../../utils/dailyTabUtils';
import { 
  cacheUserLists, 
  getCachedUserLists, 
  clearUserListsCache,
  clearListCache,
  setCacheInvalidation,
  cacheCurrentListName,
  getCachedCurrentListName,
  clearCurrentListNameCache,
  cacheCurrentListId,
  getCachedCurrentListId,
  clearCurrentListIdCache,
  generateUniqueListName
} from '../../utils/listCache';
import { sessionSaveService } from '../../services/sessionSaveService';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useModals } from '../../hooks/useModals';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useAddTicker } from '../../hooks/useAddTicker';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';
import {
  LIST_DAILY_STATISTICS_COLUMN_WIDTH_MULTIPLIER,
  LIST_DAILY_FULL_SCREEN_STATISTICS_COLUMN_WIDTH_MULTIPLIER,
} from '../../utils/stockTableColumnUtils';

// Default tickers to display - moved outside component to prevent re-creation
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

const ListDaily: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;
  
  // Check if user came from Get Started page
  const isFromGetStarted = location.state?.fromGetStarted === true;
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);
  
  // UI state
  const [isEditingTickers, setIsEditingTickers] = useState(false);
  const [tickerList, setTickerList] = useState<string[]>(() => {
    // For public lists, start with empty list - will be populated from public list data
    if (isPublicList) {
      return [];
    }
    // For authenticated users, start with empty list to avoid default ticker API calls
    if (isAuthenticated) {
      return [];
    }
    // For unauthenticated users, only use session storage if it exists, otherwise start empty
    // Default tickers should only be shown on the Landing page
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });
  const [listName, setListName] = useState<string>(() => {
    // For public lists, check sessionStorage for cached public list name
    if (isPublicList && publicUuid) {
      const cachedPublicListName = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      if (cachedPublicListName) {
        return cachedPublicListName;
      }
      // Return empty string if no cached name, will be set when data loads
      return '';
    }
    
    // For authenticated users, try to get cached list name first
    if (isAuthenticated) {
      const cachedListName = getCachedCurrentListName();
      if (cachedListName) {
        return cachedListName;
      }
    }
    
    // Fallback to session storage or default
    const savedListName = sessionStorage.getItem('stocktable-list-name');
    return savedListName || 'Main List';
  });
  
  // Ticker names and symbols from sync status API
  const [tickerNames, setTickerNames] = useState<{ [symbol: string]: string }>({});
  const [tickerSymbols, setTickerSymbols] = useState<{ [symbol: string]: string }>({});
  
  // Sync status management
  // Use list ID-specific key for authenticated users, global key for unauthenticated
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(() => {
    // Note: currentListId may not be available in initializer, so we check after mount
    const saved = sessionStorage.getItem('stocktable-syncing-tickers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  
  // Refs for polling management
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const currentAttemptRef = useRef(1);
  
  // Market status state
  const [marketStatus, setMarketStatus] = useState<'open' | 'pre-market' | 'after-hours' | 'closed' | null>(null);
  const [marketOpenTimestamp, setMarketOpenTimestamp] = useState<string | null>(null);
  const [marketCloseTimestamp, setMarketCloseTimestamp] = useState<string | null>(null);
  const [marketPreMarketTimestamp, setMarketPreMarketTimestamp] = useState<string | null>(null);
  const [cachedLatestLabel, setCachedLatestLabel] = useState<string>(getCachedLabel());
  const [cachedOpenLabel, setCachedOpenLabel] = useState<string>(() => {
    const cached = localStorage.getItem('stocktable-open-label');
    return cached || 'Open';
  });
  const [previousMarketStatus, setPreviousMarketStatus] = useState<'open' | 'pre-market' | 'after-hours' | 'closed' | null>(null);
  const [marketStatusLoading, setMarketStatusLoading] = useState(false);
  
  // Refs to track when we need to reload data
  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');

  // Store original session storage values to restore when leaving public list
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  // Track if we've attempted to fetch the public list (to prevent infinite retries)
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  
  // Set page title
  useEffect(() => {
    document.title = 'Stock Table - Daily';
  }, []);

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    // Only fetch if we haven't already attempted for this UUID, or if it's a different UUID
    const shouldFetch = publicUuid && 
                       !publicListData && 
                       !isLoadingPublicList && 
                       publicListFetchAttemptedRef.current !== publicUuid;
    
    if (shouldFetch) {
      // Save original session storage values before loading public list (only for unauthenticated users)
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics'),
        };
      }

      // Mark that we're attempting to fetch this UUID
      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);
      
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
          
          // Update ticker list from mappings
          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);
          
          // Update list name and cache it for tab switching
          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          // Cache the public list name in sessionStorage for immediate restoration on tab switch
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          
          // For 404 or 403 errors, set flag to show not found/not shared message instead of table
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
    
    // Reset the attempted ref and not found flag when publicUuid changes or becomes null
    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Restore session storage on component unmount if we were viewing a public list
  useEffect(() => {
    return () => {
      // Cleanup: restore original session storage when component unmounts if we were in public list mode
      if (originalSessionStorageRef.current && !isAuthenticated) {
        const original = originalSessionStorageRef.current;
        // Restore synchronously before component unmounts
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        originalSessionStorageRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Reset not found flag and clear error when publicUuid becomes null (e.g., when clicking logo)
  useEffect(() => {
    if (!publicUuid) {
      setIsPublicListNotFound(false);
      setPublicListError(null);
      setPublicListData(null);
    }
  }, [publicUuid]);

  // Also restore when location changes away from public list (before unmount)
  // Watch location.search directly to catch URL changes even when component doesn't unmount
  useEffect(() => {
    // If we're no longer in public list mode, restore from session storage
    if (!publicUuid && !isAuthenticated) {
      // If we have stored original values, restore them
      if (originalSessionStorageRef.current) {
        const original = originalSessionStorageRef.current;
        
        // Restore session storage
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        
        // Reset component state to match restored session storage
        if (original.tickerList !== null) {
          try {
            const restoredTickers = JSON.parse(original.tickerList);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no original data (default tickers only on Landing page)
          setTickerList([]);
        }
        
        if (original.listName !== null) {
          setListName(original.listName);
        } else {
          const savedListName = sessionStorage.getItem('stocktable-list-name');
          setListName(savedListName || 'Main List');
        }
        
        originalSessionStorageRef.current = null;
      } else {
        // If we don't have original values saved, restore from current session storage
        const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
        if (savedTickers) {
          try {
            const restoredTickers = JSON.parse(savedTickers);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no session storage (default tickers only on Landing page)
          setTickerList([]);
        }
        
        const savedListName = sessionStorage.getItem('stocktable-list-name');
        setListName(savedListName || 'Main List');
      }
      
      // Clear public list data
      setPublicListData(null);
      setPublicListError(null);
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, location.search, isAuthenticated]);

  // User list management hook (only for non-public lists)
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    setCurrentListId,
    needsUserListsReload,
    needsListDataReload,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/daily',
  });

  // List preferences hook (only for non-public lists)
  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'daily',
    persistToSessionStorageImmediately: false,
  });

  // Determine which lists are locked (beyond 3rd position for free users)
  // Free users can only access the first 3 lists (sorted alphabetically)
  const sortedUserLists = React.useMemo(() => {
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name.toLowerCase();
      const nameB = b.list_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  const lockedListIds = React.useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if current list is locked
  const isCurrentListLocked = React.useMemo(() => {
    return currentListId !== null && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // Use public list preferences if in public list mode, otherwise use private preferences
  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.daily_indicators_to_hide
    ? publicListData.listPreferences.daily_indicators_to_hide
    : privateHiddenStatistics;

  // Auto-save function for authenticated users (needed for handleSaveChanges)
  const autoSaveList = useCallback(async (tickers: string[]) => {
    // Don't auto-save in public list mode
    if (isPublicList || !isAuthenticated) return;
    
    // Determine which list ID to use
    let listId = currentListId;
    
    // If currentListId is null, try to get it from userLists or wait for it
    if (listId === null) {
      // First, try to get from cache (might be set by createDefaultMainListIfNeeded)
      const { getCachedCurrentListId } = await import('../../utils/listCache');
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        console.log('💾 Using cached list ID for auto-save:', cachedListId);
        listId = cachedListId;
      } else if (userLists.length > 0) {
        // If userLists has items, use the first list (or try to get default list)
        // Try to get the user's default list
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const defaultListExists = userLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
            if (defaultListExists) {
              listId = defaultListResponse.defaultUserTickerListId;
            } else {
              // Default list doesn't exist in userLists, use first list
              listId = userLists[0].id;
            }
          } else {
            // No default list set, use first list
            listId = userLists[0].id;
          }
        } catch (error) {
          // If we can't get default list, use first list
          console.log('⚠️ Could not get default list, using first list:', error);
          listId = userLists[0].id;
        }
      } else {
        // No lists available yet - ticker is saved to sessionStorage and will be recovered when lists load
        console.log('⚠️ Auto-save skipped: currentListId is null and no userLists available. Ticker saved to sessionStorage.');
        return;
      }
    }
    
    // Ensure listId is not null before proceeding
    if (!listId) {
      console.log('⚠️ Auto-save skipped: No list ID available');
      return;
    }
    
    try {
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1
      }));
      
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      
      // Clear the cache for this list to force reload on tab switch
      clearListCache(listId);
      
      // Mark that we need to reload list data to ensure fresh data is loaded
      needsListDataReload.current = true;
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  // Helper function to get sessionStorage key based on list ID
  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-ticker-list-${currentListId}` 
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  // Helper function to get syncing tickers sessionStorage key based on list ID
  const getSyncingTickersKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-syncing-tickers-${currentListId}` 
      : 'stocktable-syncing-tickers';
  }, [isAuthenticated, currentListId]);

  // Handle save changes (used by edit mode hook)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    // Don't allow saving changes in public list mode
    if (isPublicList) {
      return;
    }
    
    const newTickerList = [...saveTempTickerList];

    // For unauthenticated users, update sessionStorage first so recovery doesn't see stale data.
    // For authenticated users the server is the source of truth; we do not write ticker list to sessionStorage.
    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    
    // Auto-save for authenticated users
    if (isAuthenticated && currentListId) {
      try {
        // Update list name if it changed
        if (saveTempListName !== listName) {
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          
          // Update the user lists cache to reflect the name change
          setUserLists((prev: UserTickerList[]) => prev.map((list: UserTickerList) => 
            list.id === currentListId 
              ? { ...list, list_name: saveTempListName }
              : list
          ));
          
          // Cache the updated list name to prevent flickering on tab switch
          cacheCurrentListName(saveTempListName);
        }
        
        // Update ticker mappings
        await autoSaveList(newTickerList);
        
        // Refresh usage limits after successful mappings update
        try {
          await Promise.all([
            usageLimitsApi.checkTickerLimit(),
            usageLimitsApi.checkListLimit()
          ]);
        } catch (error) {
          console.error('Failed to refresh usage limits:', error);
        }
        
        // Save user list preferences for daily indicators
        await savePreferences(hiddenStatistics);
      } catch (error) {
        console.error('Failed to update list:', error);
      }
    } else {
      // Save list name to session storage for unauthenticated users
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      // Save preferences to sessionStorage (for unauthenticated users)
      await savePreferences(hiddenStatistics);
    }
    
    // Note: sessionStorage is already updated above before state changes
    // This prevents the recovery mechanism from restoring removed tickers
  }, [tickerList, listName, isAuthenticated, currentListId, setTickerList, setListName, setIsEditingTickers, hiddenStatistics, setUserLists, autoSaveList, savePreferences, getTickerListKey]);

  // Edit mode hook
  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/daily',
    onSaveChanges: handleSaveChanges,
    hiddenStatistics: !isPublicList ? { daily: privateHiddenStatistics } : undefined,
  });

  // Drag and drop hook (uses 'insert' behavior by default)
  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
  });


  // Fetch market status when component loads or refreshes
  const fetchMarketStatus = async () => {
    try {
      setMarketStatusLoading(true);
      const response = await searchApiService.getMarketStatus();
      const newMarketStatus = response.data.market_status;
      const serverMarketOpenTimestamp = response.data.market_open_timestamp;
      const serverMarketCloseTimestamp = response.data.market_close_timestamp;
      const serverMarketPreMarketTimestamp = response.data.market_pre_market_timestamp;
      
      // Use server-provided timestamps if available, otherwise track client-side as fallback
      if (serverMarketOpenTimestamp) {
        setMarketOpenTimestamp(serverMarketOpenTimestamp);
        // Also store in localStorage as fallback
        const timestamp = new Date(serverMarketOpenTimestamp).getTime();
        if (!isNaN(timestamp)) {
          localStorage.setItem('stocktable-market-open-timestamp', timestamp.toString());
        }
      } else if (previousMarketStatus === 'closed' && newMarketStatus === 'open') {
        // Fallback: Track when market status changes from 'closed' to 'open' on client
        const clientTimestamp = Date.now();
        setMarketOpenTimestamp(new Date(clientTimestamp).toISOString());
        localStorage.setItem('stocktable-market-open-timestamp', clientTimestamp.toString());
      }
      
      // Set close timestamp from server
      if (serverMarketCloseTimestamp) {
        setMarketCloseTimestamp(serverMarketCloseTimestamp);
      } else if (previousMarketStatus === 'open' && (newMarketStatus === 'closed' || newMarketStatus === 'after-hours')) {
        // Fallback: Track when market status changes from 'open' to 'closed'/'after-hours' on client
        const clientTimestamp = Date.now();
        setMarketCloseTimestamp(new Date(clientTimestamp).toISOString());
      }
      
      // Set pre-market timestamp from server
      if (serverMarketPreMarketTimestamp) {
        setMarketPreMarketTimestamp(serverMarketPreMarketTimestamp);
      } else if (previousMarketStatus !== 'pre-market' && newMarketStatus === 'pre-market') {
        // Fallback: Track when market status changes to 'pre-market' on client
        const clientTimestamp = Date.now();
        setMarketPreMarketTimestamp(new Date(clientTimestamp).toISOString());
      }
      
      setPreviousMarketStatus(newMarketStatus);
      setMarketStatus(newMarketStatus);
    } catch (error) {
      console.error('Failed to fetch market status:', error);
      setMarketStatus(null);
    } finally {
      setMarketStatusLoading(false);
    }
  };

  // Fetch market status on component mount
  useEffect(() => {
    fetchMarketStatus();
  }, []);

  // Persist syncingTickers to sessionStorage whenever it changes
  // Use list ID-specific key for authenticated users
  useEffect(() => {
    const key = getSyncingTickersKey();
    if (syncingTickers.size > 0) {
      sessionStorage.setItem(key, JSON.stringify(Array.from(syncingTickers)));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [syncingTickers, getSyncingTickersKey]);

  // Function to check sync status and handle synced/unsynced tickers
  const checkSyncAndSave = useCallback(async (tickersToCheck: string[], attemptNumber: number = 1) => {
    if (tickersToCheck.length === 0) return;
    
    // Guard: Prevent concurrent execution
    if (isCheckingSyncRef.current) {
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped
    if (pollingStoppedRef.current) {
      isCheckingSyncRef.current = false;
      return;
    }
    
    isCheckingSyncRef.current = true;
    
    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: tickersToCheck,
      });
      
      // Store ticker names and symbols from sync response
      const newTickerNames: { [symbol: string]: string } = {};
      const newTickerSymbols: { [symbol: string]: string } = {};
      syncResponse.data.tickers.forEach((ticker: any) => {
        if (ticker.ticker_name) {
          newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
          sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
        }
        if (ticker.ticker_symbol) {
          newTickerSymbols[ticker.ticker_symbol] = ticker.ticker_symbol;
          sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
        }
      });
      setTickerNames(prev => ({ ...prev, ...newTickerNames }));
      setTickerSymbols(prev => ({ ...prev, ...newTickerSymbols }));
      
      // Separate tickers into synced and unsynced
      const newlySyncedTickers: string[] = [];
      const stillUnsyncedTickers: string[] = [];
      
      syncResponse.data.tickers.forEach((ticker: any) => {
        if (ticker.is_synced) {
          newlySyncedTickers.push(ticker.ticker_symbol);
        } else {
          stillUnsyncedTickers.push(ticker.ticker_symbol);
        }
      });
      
      // Remove synced tickers from syncing and unsynced sets
      if (newlySyncedTickers.length > 0) {
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          newlySyncedTickers.forEach(ticker => newSet.delete(ticker));
          return newSet;
        });
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          newlySyncedTickers.forEach(ticker => newSet.delete(ticker));
          return newSet;
        });
        
        // Save synced tickers to database if authenticated
        if (isAuthenticated && currentListId !== null && newlySyncedTickers.length > 0) {
          try {
            const mergedList = Array.from(new Set([...newlySyncedTickers, ...tickerList]));
            
            // Only save if the merged list is actually different from current tickerList
            // This prevents unnecessary API calls when tickers are already in the list
            const currentListSet = new Set(tickerList);
            const isDifferent = mergedList.length !== tickerList.length || 
                               !newlySyncedTickers.every(ticker => currentListSet.has(ticker));
            
            if (isDifferent) {
              await autoSaveList(mergedList);
            }
          } catch (error) {
            console.error('Error saving newly synced tickers to database:', error);
          }
        }
      }
      
      // Update unsynced tickers set
      if (stillUnsyncedTickers.length > 0) {
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          stillUnsyncedTickers.forEach(ticker => newSet.add(ticker));
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error checking sync status:', error);
    } finally {
      isCheckingSyncRef.current = false;
    }
  }, [isAuthenticated, currentListId, tickerList, autoSaveList]);

  // Check sync status when tickerList changes to get ticker names and identify unsynced tickers
  useEffect(() => {
    if (tickerList.length === 0) {
      // Clear unsynced tickers if list is empty
      setUnsyncedTickers(new Set());
      return;
    }
    
    // Reset polling state
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    
    // Check sync status for all tickers in the list
    checkSyncAndSave(tickerList, 1);
  }, [tickerList, checkSyncAndSave]); // Run when tickerList changes to catch new tickers

  // Polling for unsynced tickers every 2 seconds
  useEffect(() => {
    if (unsyncedTickers.size === 0) {
      // Clear interval if no unsynced tickers
      if (syncPollingIntervalRef.current) {
        clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
      }
      return;
    }
    
    // Don't start polling if already polling
    if (syncPollingIntervalRef.current) {
      return;
    }
    
    // Clear any existing interval
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
    }
    
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    
    const pollingDelay = 2000; // 2 seconds
    
    const interval = setInterval(() => {
      // Check if polling was stopped
      if (pollingStoppedRef.current) {
        clearInterval(interval);
        syncPollingIntervalRef.current = null;
        return;
      }
      
      // Get the next attempt number
      const nextAttempt = currentAttemptRef.current + 1;
      
      // Check if we've exceeded the limit (5 attempts = 10 seconds)
      if (nextAttempt > 5) {
        clearInterval(interval);
        syncPollingIntervalRef.current = null;
        pollingStoppedRef.current = true;
        return;
      }
      
      // Update the ref for the next iteration
      currentAttemptRef.current = nextAttempt;
      
      // Check current unsynced tickers from state
      setUnsyncedTickers(currentUnsynced => {
        if (currentUnsynced.size > 0) {
          checkSyncAndSave(Array.from(currentUnsynced), nextAttempt);
        } else {
          clearInterval(interval);
          syncPollingIntervalRef.current = null;
          pollingStoppedRef.current = true;
        }
        return currentUnsynced;
      });
    }, pollingDelay);
    
    syncPollingIntervalRef.current = interval;
    
    return () => {
      if (syncPollingIntervalRef.current) {
        clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
      }
    };
  }, [unsyncedTickers.size, checkSyncAndSave]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollingIntervalRef.current) {
        clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
      }
      pollingStoppedRef.current = true;
    };
  }, []);

  // When syncingTickers changes (new ticker added), check if it needs to be added to unsyncedTickers
  useEffect(() => {
    if (syncingTickers.size === 0) return;
    
    // Find tickers in syncingTickers that are not in unsyncedTickers yet
    const tickersToCheck = Array.from(syncingTickers).filter(ticker => !unsyncedTickers.has(ticker));
    
    if (tickersToCheck.length > 0) {
      // Check their sync status and add to unsyncedTickers if still syncing
      setTimeout(async () => {
        try {
          const api = isAuthenticated ? userTickerApi : tickerApi;
          const syncResponse = await api.checkTickerSyncStatus({
            ticker_symbols: tickersToCheck,
          });
          
          const stillSyncing = syncResponse.data?.tickers
            ?.filter((t: any) => !t.is_synced)
            .map((t: any) => t.ticker_symbol) || [];
          
          if (stillSyncing.length > 0) {
            setUnsyncedTickers(prev => {
              const newSet = new Set(prev);
              stillSyncing.forEach((ticker: string) => newSet.add(ticker));
              return newSet;
            });
            console.log(`ListDaily: Added ${stillSyncing.length} new syncing tickers to unsyncedTickers:`, stillSyncing);
          }
        } catch (error) {
          console.error('ListDaily: Error checking sync status for new tickers:', error);
        }
      }, 500); // Small delay to avoid race conditions
    }
  }, [syncingTickers, unsyncedTickers, isAuthenticated]);

  // Ensure tickerList is always deduplicated using a ref to prevent infinite loops
  const lastTickerListRef = useRef<string[]>([]);
  useEffect(() => {
    if (tickerList.length > 0) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      // Only update if there are duplicates AND the unique list is different from last processed list
      if (uniqueTickerList.length !== tickerList.length && 
          JSON.stringify(uniqueTickerList) !== JSON.stringify(lastTickerListRef.current)) {
        lastTickerListRef.current = uniqueTickerList;
        setTickerList(uniqueTickerList);
      } else if (uniqueTickerList.length === tickerList.length) {
        // Update ref even if no deduplication needed
        lastTickerListRef.current = uniqueTickerList;
      }
    }
  }, [tickerList]);

  // Determine which ticker list to use for display - ensure it's deduplicated
  const displayTickerList = Array.from(new Set(isEditingTickers ? tempTickerList : tickerList));

  // Determine locked tickers (beyond 10th position for free users OR all tickers if list is locked)
  // Free users can only access the first 10 tickers in a list
  // If the list itself is locked, all tickers are locked
  // For public lists, use owner's subscription status instead of viewer's
  // Note: Calculate after tempTickerList is available from useEditMode hook
  const lockedTickers = React.useMemo(() => {
    if (isCurrentListLocked) {
      // If list is locked, lock all tickers
      return displayTickerList;
    }
    // For public lists, use owner's subscription status
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription 
        ? [] // Owner has PRO, so no locked tickers
        : displayTickerList.slice(10); // Owner doesn't have PRO, apply same locks
    }
    // For private lists, use viewer's subscription status
    return (!isAuthenticated || !user?.hasProSubscription) 
      ? displayTickerList.slice(10) // Tickers beyond the 10th position (index 10+)
      : [];
  }, [displayTickerList, isAuthenticated, user?.hasProSubscription, isCurrentListLocked, isPublicList, publicListData]);

  // Use shared hook for Daily tab statistics
  const visibleStatistics = useDailyTabStatistics({
    marketStatus,
    isEditingTickers,
    cachedLatestLabel,
    cachedOpenLabel,
    marketOpenTimestamp,
    marketCloseTimestamp,
    marketPreMarketTimestamp,
  });

  // data hooks - use tickerList directly since authenticated users start with empty list
  const { data: tickerData, loading, error, lastUpdated, refetch, shouldShowRefresh } = useTickerSnapshot(tickerList, isAuthenticated, marketStatus || undefined);

  // Store refetch function in a ref so it can be accessed in merge effect
  const refetchTickerDataRef = useRef(refetch);
  useEffect(() => {
    refetchTickerDataRef.current = refetch;
  }, [refetch]);

  // Determine if we should show refresh functionality based on market status
  const shouldShowRefreshUI = marketStatus !== 'closed';

  // Refetch market status when ticker data is refreshed
  useEffect(() => {
    if (shouldShowRefresh) {
      fetchMarketStatus();
    }
  }, [shouldShowRefresh]);
  
  // Helper function to check if data is stale (more than 15 minutes old) - consistent with useTickerSnapshot
  const isDataStale = (updatedTimestamp: number): boolean => {
    // Validate the timestamp
    if (!updatedTimestamp || isNaN(updatedTimestamp)) {
      return false;
    }
    
    // Convert from nanoseconds to milliseconds if needed
    let timestampInMs = updatedTimestamp;
    if (updatedTimestamp > 1e15) { // If timestamp is larger than 1e15, it's likely in nanoseconds
      timestampInMs = updatedTimestamp / 1e6; // Convert nanoseconds to milliseconds
    }
    
    const updated = new Date(timestampInMs);
    
    // Check if the date is valid
    if (isNaN(updated.getTime())) {
      return false;
    }
    
    const now = new Date();
    const timeDifferenceMs = now.getTime() - updated.getTime();
    const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60); // Convert to minutes
    
    // If the timestamp is in the future, consider it fresh (not stale)
    if (timeDifferenceMs < 0) {
      return false;
    }
    
    return timeDifferenceMinutes > 30; // More than 30 minutes old
  };
  

  const getTickerData = (symbol: string): TickerDisplayData | undefined => {
    const tickerDataForSymbol = tickerData.find(t => t.ticker === symbol);
    
    // Get ticker name from sessionStorage cache (same pattern as other components)
    const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
    const tickerName = cachedName || tickerNames[symbol] || symbol;
    const tickerSymbol = cachedSymbol || tickerSymbols[symbol] || symbol;
    
    if (!tickerDataForSymbol) {
      return undefined;
    }
    
    return {
      ...tickerDataForSymbol,
      ticker_name: tickerName,
      ticker_symbol: tickerSymbol,
    };
  };

  // Statistic formatting and color logic using shared utilities
  const formatStatisticValue = (statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    return formatDailyStatisticValue(statisticKey, ticker, {
      marketStatus,
      isEditingTickers,
    });
  };

  const getStatisticColor = (statisticKey: string, ticker: TickerDisplayData): string => {
    return getDailyStatisticColor(statisticKey, ticker, {
      marketStatus,
      isEditingTickers,
    });
  };

  // persist ticker list (only for non-authenticated users, and not for public lists)
  useEffect(() => {
    if (!isAuthenticated && !isPublicList) {
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
    }
  }, [tickerList, isAuthenticated, isPublicList]);



  // user activity tracking
  useEffect(() => {
    let lastActivity = Date.now();
    const CHECK_INTERVAL = 10 * 1000; // 10s

    const updateActivity = () => {
      lastActivity = Date.now();
    };
    const checkActivity = () => {
      // Auto-refresh logic removed - no longer needed
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, updateActivity, true));
    const interval = setInterval(checkActivity, CHECK_INTERVAL);
    return () => {
      clearInterval(interval);
      events.forEach(e => document.removeEventListener(e, updateActivity, true));
    };
  }, []);

  // Register current state with the global session save service
  useEffect(() => {
    if (!isAuthenticated) {
      sessionSaveService.registerSessionData({
        tickerList,
        listName,
        hiddenStatistics: {
          daily: hiddenStatistics
        }
      });
    }
  }, [tickerList, listName, hiddenStatistics, isAuthenticated]);

  // Ref to prevent duplicate merges
  const hasMergedSessionStorageRef = useRef(false);
  // Ref to prevent recoverPendingTickers from running multiple times
  const hasRecoveredPendingTickersRef = useRef(false);
  
  // Recovery: Merge tickers from sessionStorage into tickerList immediately (for authenticated users)
  // Merge from sessionStorage only when unauthenticated. Authenticated users use server as source of truth.
  useEffect(() => {
    if (isAuthenticated) return;

    const mergeSessionStorageTickers = () => {
      if (currentListId === null) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only merge tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) return;
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        // Deduplicate sessionStorage tickers first
        const uniqueSessionTickers = Array.from(new Set(sessionTickers));
        // Deduplicate current tickerList
        const uniqueTickerList = Array.from(new Set(tickerList));
        // Find tickers that are in sessionStorage but not in current list
        const missingTickers = uniqueSessionTickers.filter(ticker => !uniqueTickerList.includes(ticker));
        
        if (missingTickers.length === 0) return;
        
        // Immediately merge them into tickerList so they show up with loading state
        // Ensure the final list has no duplicates
        const mergedList = Array.from(new Set([...uniqueTickerList, ...missingTickers]));
        setTickerList(mergedList);
        
        // Trigger data fetch for the new tickers after a short delay to ensure state is updated
        // useTickerSnapshot should automatically fetch when tickerList changes, but we explicitly
        // call refetch to ensure it happens immediately
        setTimeout(() => {
          if (refetchTickerDataRef.current) {
            refetchTickerDataRef.current();
          }
        }, 300);
      } catch (error) {
        console.error('Error merging sessionStorage tickers:', error);
      }
    };

    if (!hasMergedSessionStorageRef.current) {
      const timeoutId = setTimeout(() => {
        mergeSessionStorageTickers();
        hasMergedSessionStorageRef.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    const intervalId = setInterval(() => {
      if (currentListId !== null && tickerList.length > 0 && hasMergedSessionStorageRef.current) {
        mergeSessionStorageTickers();
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, currentListId, tickerList, setTickerList, getTickerListKey]);
  
  // Reset merge flag when list ID changes
  useEffect(() => {
    hasMergedSessionStorageRef.current = false;
  }, [currentListId]);

  // Recovery: Check for tickers in sessionStorage that need to be synced to database (unauthenticated only)
  useEffect(() => {
    if (isAuthenticated) return;
    if (hasRecoveredPendingTickersRef.current) return;

    const recoverPendingTickers = async () => {
      if (!currentListId || tickerList.length === 0) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only recover tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) {
        hasRecoveredPendingTickersRef.current = true;
        return;
      }
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        const missingTickers = sessionTickers.filter(ticker => !tickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // No missing tickers - mark as recovered and clear sessionStorage if everything is already saved
          hasRecoveredPendingTickersRef.current = true;
          // Clear sessionStorage since everything is already in the database
          sessionStorage.removeItem(getTickerListKey());
          return;
        }
        
        // Check if these tickers have been synced (exist in tickerBasics)
        const api = userTickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: missingTickers,
        });
        
        const syncedTickers = syncResponse.data?.tickers
          ?.filter((t: any) => t.is_synced === true)
          .map((t: any) => t.ticker_symbol) || [];
        
        if (syncedTickers.length === 0) {
          // Don't mark as recovered yet - we'll retry later
          return;
        }
        
        // Mark as recovered since we're processing the recovery now
        hasRecoveredPendingTickersRef.current = true;
        
        // Merge synced tickers with current list and save to database
        const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
        
        // Only save if the merged list is actually different from current tickerList
        // This prevents unnecessary API calls on page refresh when nothing has changed
        const currentListSet = new Set(tickerList);
        const isDifferent = mergedList.length !== tickerList.length || 
                           !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
        
        if (isDifferent) {
          await autoSaveList(mergedList);
          // Update local state
          setTickerList(mergedList);
          // Clear sessionStorage after successful save
          sessionStorage.removeItem(getTickerListKey());
        } else {
          // Clear sessionStorage since everything is already saved
          sessionStorage.removeItem(getTickerListKey());
        }
      } catch (error) {
        console.error('Error recovering pending tickers:', error);
        // Mark as recovered even on error to prevent infinite retries
        hasRecoveredPendingTickersRef.current = true;
      }
    };

    // Only run recovery once after initial load, with a delay to ensure database list is loaded
    const timeoutId = setTimeout(recoverPendingTickers, 1500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, autoSaveList, setTickerList, getTickerListKey]);
  
  // Reset recovery flag when list ID changes
  useEffect(() => {
    hasRecoveredPendingTickersRef.current = false;
  }, [currentListId]);

  // Update labels when market status is determined and cache them
  useEffect(() => {
    if (marketStatus !== null) {
      const latestLabel = getLatestLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
      const openLabel = getOpenLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
      setCachedLatestLabel(latestLabel);
      setCachedOpenLabel(openLabel);
      // Cache the labels for next time
      localStorage.setItem('stocktable-latest-label', latestLabel);
      localStorage.setItem('stocktable-open-label', openLabel);
    }
  }, [marketStatus, marketOpenTimestamp, marketCloseTimestamp]);

  // Ticker add/remove using useAddTicker hook
  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>> | undefined,
    isEditingTickers,
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string, tickerSymbol?: string) => {
      if (tickerName) {
        setTickerNames(prev => ({
          ...prev,
          [ticker]: tickerName
        }));
        sessionStorage.setItem(`ticker-name-${ticker}`, tickerName);
      }
      if (tickerSymbol) {
        setTickerSymbols(prev => ({
          ...prev,
          [ticker]: tickerSymbol
        }));
        sessionStorage.setItem(`ticker-symbol-${ticker}`, tickerSymbol);
      }
    }, []),
    autoSaveList,
    saveListImmediately: useCallback(async (tickers: string[]) => {
      if (isPublicList) return;
      const uniqueTickers = Array.from(new Set(tickers));

      // Authenticated: use server as source of truth so list stays in sync across devices
      if (isAuthenticated && !isPublicList) {
        let listId = currentListId;
        if (listId === null) {
          const cachedListId = getCachedCurrentListId();
          if (cachedListId) listId = cachedListId;
          else if (userLists.length > 0) {
            try {
              const defaultListResponse = await userApi.getDefaultList();
              if (defaultListResponse.defaultUserTickerListId && userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)) {
                listId = defaultListResponse.defaultUserTickerListId;
              } else {
                listId = userLists[0].id;
              }
            } catch {
              listId = userLists[0].id;
            }
          }
        }
        if (listId != null) {
          try {
            const tickerMappings = uniqueTickers.map((ticker, index) => ({ ticker_symbol: ticker, order_num: index + 1 }));
            await userTickerListApi.updateTickerMappings(listId, tickerMappings);
            clearListCache(listId);
            needsListDataReload.current = true;
          } catch (error) {
            console.error('saveListImmediately: failed to save to server', error);
          }
        }
        return;
      }

      // Unauthenticated: persist to sessionStorage only
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickers));
      if (currentListId !== null) {
        sessionStorage.setItem(`stocktable-ticker-list-${currentListId}`, JSON.stringify(uniqueTickers));
      }
    }, [currentListId, isPublicList, isAuthenticated, userLists]),
    pollingInterval: 1000,
    maxPollAttempts: 1, // Set to 1 - ListDaily's own polling will handle subsequent checks
    normalizeTicker: (ticker: string) => ticker, // Keep original case
    onSyncComplete: useCallback(async (ticker: string, tickerStatus: any) => {
      // When ticker finishes syncing, ensure it's removed from unsyncedTickers and trigger data refetch
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticker);
        return newSet;
      });
      // Trigger data refetch for the newly synced ticker
      if (refetchTickerDataRef.current) {
        refetchTickerDataRef.current();
      }
    }, []),
  });
  const handleRemoveTicker = async (tickerToRemove: string) => {
    if (isEditingTickers) {
      const newTempList = tempTickerList.filter(t => t !== tickerToRemove);
      setTempTickerList(newTempList);
      // No auto-save in edit mode - will be saved when "Done" is clicked
    } else {
      const newList = tickerList.filter(t => t !== tickerToRemove);
      setTickerList(newList);

      // Authenticated: server is source of truth, only persist to server (no sessionStorage)
      if (isAuthenticated && !isPublicList) {
        await autoSaveList(newList);
      }
    }
  };


  // Hidden statistics handlers


  // Handle ticker navigation
  const handleTickerClick = (tickerSymbol: string) => {
    if (isPublicList && publicUuid) {
      navigate(`/ticker/${tickerSymbol}/daily?public_uuid=${publicUuid}`);
    } else {
      navigate(`/ticker/${tickerSymbol}/daily`);
    }
  };

  // Handle refresh usage limits callback
  const handleRefreshUsageLimits = (refreshFn: () => void) => {
    refreshUsageLimitsRef.current = refreshFn;
  };

  // Share modal handlers
  const handleShareList = () => {
    setIsShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  // Handle indicator info click
  const handleIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedIndicatorKey(indicatorKey);
    setIsIndicatorModalOpen(true);
  };

  // Handle indicator modal close
  const handleCloseIndicatorModal = () => {
    setIsIndicatorModalOpen(false);
    setSelectedIndicatorKey('');
  };

  const handleShareModalUpdate = async () => {
    // Refresh user lists to get updated share settings
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
        // Optionally refresh the current list data
        const updatedList = updatedLists.find(list => list.id === currentListId);
        if (updatedList) {
          cacheUserLists(updatedLists);
        }
      } catch (error) {
        console.error('Failed to refresh lists after share update:', error);
      }
    }
  };

  // Get current list data for share modal
  const currentList = currentListId && userLists
    ? userLists.find(list => list.id === currentListId) || null
    : null;

  // Modals hook (must be after handleListChange is defined)
  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setCurrentListId(null);
      setListName('Main List');
      setTickerList([]);
      setHiddenStatistics([]);
    },
  });

  // Tabs component
  const tabs = (
    <TabBar 
      activeTab="daily" 
      disabled={isEditingTickers} 
      publicUuid={publicUuid}
    />
  );

  // If public list is not found, show simple centered message
  // Also check publicUuid directly to ensure we exit this state when URL changes
  if (publicUuid && isPublicListNotFound) {
    return (
      <>
        <div className="flex flex-col min-h-screen">
          <Header />
          {!isAuthenticated && <PublicListPromoBar />}
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-text-primary">
                {publicListError || 'This list does not exist or is no longer available.'}
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-8">
            <Footer />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <div className="flex flex-col min-h-screen">
    <StockTableLayout
      tickerList={displayTickerList}
      tickerData={tickerData}
      loading={loading || isLoadingPublicList}
      error={error || (isPublicList ? publicListError : null)}
      lastUpdated={lastUpdated}
      isEditingTickers={isPublicList ? false : isEditingTickers}
      isPublicList={isPublicList}
      showPublicListPromoBar={isPublicList && !isAuthenticated}
      hiddenStatistics={hiddenStatistics}
      lockedTickers={lockedTickers}
      draggedTicker={draggedTicker}
      dragOverTicker={dragOverTicker}
      droppedTicker={droppedTicker}
      statistics={visibleStatistics}
      getTickerData={getTickerData}
      formatStatisticValue={formatStatisticValue}
      getStatisticColor={getStatisticColor}
      onRefetch={refetch}
      onTickerClick={handleTickerClick}
      onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
      onDragStart={isPublicList ? () => {} : handleDragStart}
      onDragOver={isPublicList ? () => {} : handleDragOver}
      onDragLeave={isPublicList ? () => {} : handleDragLeave}
      onDrop={isPublicList ? () => {} : handleDrop}
      onDragEnd={isPublicList ? () => {} : handleDragEnd}
      onHideStatistic={isPublicList ? () => {} : handleHideStatistic}
      onShowStatistic={isPublicList ? () => {} : handleShowStatistic}
      onIndicatorInfoClick={handleIndicatorInfoClick}
      indicatorKeysWithInfo={['volume']}
      listName={listName}
      onStartEdit={isPublicList ? () => {} : handleStartEdit}
      onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
      onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
      onAddTicker={isPublicList ? () => {} : handleAddTicker}
      onSave={handleSave}
      tempListName={tempListName}
      onTempListNameChange={setTempListName}
      userLists={isPublicList ? [] : userLists}
      currentListId={isPublicList ? undefined : (currentListId || undefined)}
      onListChange={isPublicList ? undefined : handleListChange}
      onCreateNewList={isPublicList ? undefined : handleCreateNewList}
      onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
      onDeleteList={handleDeleteList}
      showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
      onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
      onRefreshUsageLimits={handleRefreshUsageLimits}
      showPromotionalTooltip={isFromGetStarted}
      tabs={tabs}
      showRefreshButton={shouldShowRefreshUI}
      statisticsColumnWidthMultiplier={LIST_DAILY_STATISTICS_COLUMN_WIDTH_MULTIPLIER}
      fullScreenStatisticsColumnWidthMultiplier={LIST_DAILY_FULL_SCREEN_STATISTICS_COLUMN_WIDTH_MULTIPLIER}
    />
    
    {/* Footer */}
    <div className="mt-4 sm:mt-8">
      <Footer />
    </div>
    </div>
    
    {/* Delete Confirmation Modal */}
    <DeleteListConfirmationModal
      isOpen={isDeleteModalOpen}
      onClose={handleDeleteCancel}
      onConfirm={handleDeleteConfirm}
      listName={listName}
    />
    
    {/* Error Dialog */}
    <ErrorDialog
      isOpen={isErrorDialogOpen}
      onClose={handleErrorDialogClose}
      title="Error"
      message={errorMessage}
    />
    
    {/* Share List Modal */}
      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
        isListLocked={isCurrentListLocked}
      />
    
    {/* Indicator Info Modal */}
    <IndicatorInfoModal
      isOpen={isIndicatorModalOpen}
      onClose={handleCloseIndicatorModal}
      indicatorKey={selectedIndicatorKey}
    />
    </>
  );
};

export default ListDaily;
